// Microbe RTS — fog of war (presentational).
//
// The player only sees the map their own units + owned nodes currently light up. We keep a coarse
// grid over the world: each cell is CURRENTLY-VISIBLE, EXPLORED (seen before, now dark), or HIDDEN
// (never seen). A small offscreen texture encodes a dark veil at per-cell alpha; drawImage scales
// it up with smoothing so the fog has soft edges. `isVisible` samples the same grid so rival + wild
// units are culled exactly where the veil would hide them.
//
// This never touches the sim — fog is pure rendering. (The bot already has its own omniscience knob.)

import { WORLD, VISION } from '../config.js';
import type { GameState, Vec, Side } from '../types.js';

export interface Fog {
  update(state: GameState): void;
  isVisible(p: Vec): boolean;
  isExplored(p: Vec): boolean;
  /** Draw the fog veil over the world (main view). */
  paint(g: CanvasRenderingContext2D): void;
  /** Draw the same veil texture into an arbitrary dest rect (e.g. the minimap ribbon). */
  drawVeil(g: CanvasRenderingContext2D, dx: number, dy: number, dw: number, dh: number): void;
}

export function createFog(viewSide: Side = 'you'): Fog {
  const cell = VISION.cell;
  const cols = Math.max(1, Math.ceil(WORLD.width / cell));
  const rows = Math.max(1, Math.ceil(WORLD.height / cell));
  const visible = new Uint8Array(cols * rows);
  const explored = new Uint8Array(cols * rows);

  const tex = document.createElement('canvas');
  tex.width = cols;
  tex.height = rows;
  const tg = tex.getContext('2d');
  const img = tg ? tg.createImageData(cols, rows) : null;

  function stamp(x: number, y: number, r: number): void {
    const r2 = r * r;
    const c0 = Math.max(0, Math.floor((x - r) / cell));
    const c1 = Math.min(cols - 1, Math.floor((x + r) / cell));
    const r0 = Math.max(0, Math.floor((y - r) / cell));
    const r1 = Math.min(rows - 1, Math.floor((y + r) / cell));
    for (let cy = r0; cy <= r1; cy++) {
      const ccy = (cy + 0.5) * cell;
      for (let cx = c0; cx <= c1; cx++) {
        const ccx = (cx + 0.5) * cell;
        const dx = ccx - x;
        const dy = ccy - y;
        if (dx * dx + dy * dy <= r2) {
          const idx = cy * cols + cx;
          visible[idx] = 1;
          explored[idx] = 1;
        }
      }
    }
  }

  function update(state: GameState): void {
    visible.fill(0);
    for (const u of state.units) {
      if (u.side !== viewSide || u.hp <= 0) continue;
      const r = VISION.unit[u.kind] ?? 260;
      stamp(u.pos.x, u.pos.y, r);
    }
    for (const n of state.nodes) {
      if (n.owner !== viewSide) continue;
      stamp(n.pos.x, n.pos.y, n.type === 'home' ? VISION.homeNode : VISION.node);
    }
    if (!img) return;
    const d = img.data;
    for (let i = 0; i < cols * rows; i++) {
      const o = i * 4;
      // A cold near-black veil; alpha carries the fog strength.
      d[o] = 4;
      d[o + 1] = 8;
      d[o + 2] = 14;
      let a = 0;
      if (!visible[i]) a = explored[i] ? VISION.explored : VISION.hidden;
      d[o + 3] = Math.round(a * 255);
    }
    tg!.putImageData(img, 0, 0);
  }

  function cellIndex(p: Vec): number {
    const cx = Math.min(cols - 1, Math.max(0, Math.floor(p.x / cell)));
    const cy = Math.min(rows - 1, Math.max(0, Math.floor(p.y / cell)));
    return cy * cols + cx;
  }
  function isVisible(p: Vec): boolean {
    return visible[cellIndex(p)] === 1;
  }
  function isExplored(p: Vec): boolean {
    return explored[cellIndex(p)] === 1;
  }

  // The texture is a hair larger than the world (cols/rows are ceil'd), so sample only the exact
  // world region — WORLD.{width,height}/cell texels — into the dest rect. That makes each texel map
  // to exactly `cell` world units on BOTH axes, so the veil lines up with the isVisible/isExplored
  // grid on the main view AND scales cleanly onto the minimap ribbon (which is exactly world-sized).
  const srcW = WORLD.width / cell;
  const srcH = WORLD.height / cell;
  function drawVeil(g: CanvasRenderingContext2D, dx: number, dy: number, dw: number, dh: number): void {
    if (!tg) return;
    g.save();
    g.imageSmoothingEnabled = true;
    g.drawImage(tex, 0, 0, srcW, srcH, dx, dy, dw, dh);
    g.restore();
  }

  function paint(g: CanvasRenderingContext2D): void {
    drawVeil(g, 0, 0, WORLD.width, WORLD.height);
  }

  return { update, isVisible, isExplored, paint, drawVeil };
}
